// Capture a Moments screenshot.
//
// Two paths depending on context:
//
// 1. WebGL-canvas path (preferred, used whenever the 3D arena is up)
//    Reads the Three.js canvas's framebuffer via canvas.toBlob().
//    preserveDrawingBuffer:true on the renderer (see tableScene.js)
//    keeps the most recent rendered frame available. Pros:
//      - Panels (Moments, mailbox, chat, dock) stay visibly open;
//        none of them are in the DOM compositor path that the
//        capture reads from.
//      - No timing games, no overlay hide/show. The flash + shutter
//        sound is purely cosmetic, played AFTER the pixels are
//        already in our hands.
//      - Works on any OS, no extra permissions.
//    Cons:
//      - Captures only the 3D scene. DOM HUD elements (life totals,
//        turn indicators) aren't included. Acceptable: those are
//        context for the moment, the 3D scene IS the moment.
//
// 2. capturePage() fallback (hub / menus without a game canvas)
//    Main-process grab of the entire window. Has to hide our own
//    overlays so the screenshot isn't "Moments panel taking a
//    picture of itself." The flash masks the ~300 ms overlay hide
//    so the user's eye doesn't dwell on it.
//
// Output shape is identical for both paths: a downscaled WebP blob
// plus dimensions, so callers don't care which path ran.

import { playShutterCosmetic } from './shutterFx';
import { isWeb } from '../platform';

// 720 px was too aggressive. Game UI text became unreadable at
// typical lightbox display sizes (1600 px wide on 2K, 2000 px+ on 4K).
// 1600 long-edge keeps readability crisp without pushing files past
// the server's per-post byte cap; q=0.80 preserves fine strokes in
// card art + HUD text without ballooning size.
const WEBP_QUALITY = 0.80;
const MAX_LONG_EDGE = 1600;

function sleepFrames(n) {
  let p = Promise.resolve();
  for (let i = 0; i < n; i++) {
    p = p.then(() => new Promise((r) => requestAnimationFrame(r)));
  }
  return p;
}

// ---- WebGL canvas path ----------------------------------------------------

function canvasToWebP(canvas) {
  // Downscale on the fly through an intermediate 2D canvas so the
  // encoded blob is the same ~720 px long-edge size as the
  // capturePage path, giving a predictable bandwidth cost.
  const longEdge = Math.max(canvas.width, canvas.height);
  const scale = longEdge > MAX_LONG_EDGE ? MAX_LONG_EDGE / longEdge : 1;
  const outW = Math.max(1, Math.round(canvas.width * scale));
  const outH = Math.max(1, Math.round(canvas.height * scale));

  const out = document.createElement('canvas');
  out.width = outW;
  out.height = outH;
  const ctx = out.getContext('2d');
  // drawImage on a WebGL canvas reads its most recent preserved frame.
  ctx.drawImage(canvas, 0, 0, outW, outH);

  return new Promise((resolve, reject) => {
    out.toBlob(
      (blob) => {
        if (!blob) return reject(new Error('WebP encode returned null.'));
        resolve({ blob, width: outW, height: outH, bytes: blob.size });
      },
      'image/webp',
      WEBP_QUALITY,
    );
  });
}

function isUsableCanvas(canvas) {
  if (!canvas) return false;
  if (canvas.width <= 0 || canvas.height <= 0) return false;
  // Quick sanity check: read the middle pixel. If the framebuffer is
  // all-black, the game probably isn't rendering yet and we shouldn't
  // pretend a screenshot happened.
  try {
    const probe = document.createElement('canvas');
    probe.width = 1;
    probe.height = 1;
    const c = probe.getContext('2d');
    c.drawImage(canvas, canvas.width / 2, canvas.height / 2, 1, 1, 0, 0, 1, 1);
    const [r, g, b, a] = c.getImageData(0, 0, 1, 1).data;
    return a > 0 && (r + g + b) > 4;
  } catch {
    return false;
  }
}

async function captureFromCanvasPath() {
  const canvas = typeof window !== 'undefined' ? window.__valkCaptureCanvas : null;
  if (!canvas) return null;
  // The board renderer runs without preserveDrawingBuffer, so the canvas is
  // only readable in the same task as a composite. Render, then read.
  window.__valkCaptureRender?.();
  if (!isUsableCanvas(canvas)) return null;
  return canvasToWebP(canvas);
}

// ---- capturePage fallback -------------------------------------------------

// Hide every [data-screenshot-ignore] element by physically detaching
// it from the DOM. display:none and visibility:hidden both left
// animated panels lingering in the compositor frame that capturePage
// samples. DOM detach is the only technique that gives a 100%
// guarantee Chromium can't paint the element into the capture.
//
// React/Preact don't re-render when we detach + reattach: the virtual
// tree is unchanged, only the live DOM node's parent link moves. That
// keeps framer-motion's animation state intact so panels don't replay
// their mount animations on restore.
function hideOverlays() {
  const overlays = Array.from(document.querySelectorAll('[data-screenshot-ignore]'));
  const restore = [];

  for (const el of overlays) {
    const parent = el.parentNode;
    if (!parent) continue;
    const next = el.nextSibling;
    parent.removeChild(el);
    restore.push(() => {
      try {
        if (next && next.parentNode === parent) parent.insertBefore(el, next);
        else parent.appendChild(el);
      } catch {
        // If the parent unmounted mid-capture (unlikely but possible
        // e.g. on logout) there's nothing to restore to. Swallow.
      }
    });
  }

  // Force reflow so the detachment commits before the compositor
  // samples the frame.
  void document.documentElement.offsetHeight;

  if (import.meta.env?.DEV) {
    console.log('[moments] hideOverlays detached', overlays.length, 'elements');
  }

  return () => {
    for (const fn of restore) fn();
  };
}

async function fetchCaptureJpeg() {
  const res = await fetch('/api/moments/capture', { method: 'POST' });
  const contentType = res.headers.get('content-type') || '';
  if (!res.ok) {
    const body = await res.text().catch(() => '');
    throw new Error(`Capture failed (${res.status}): ${body.slice(0, 200)}`);
  }
  if (!contentType.startsWith('image/')) {
    const body = await res.text().catch(() => '');
    throw new Error(`Capture returned ${contentType || 'no content-type'}: ${body.slice(0, 200)}`);
  }
  const blob = await res.blob();
  if (!blob || blob.size === 0) {
    throw new Error('Capture returned an empty image (window may not be ready yet).');
  }
  return blob;
}

async function decodeToImage(blob) {
  const url = URL.createObjectURL(blob);
  try {
    const img = new Image();
    img.src = url;
    await img.decode();
    return { img, width: img.naturalWidth, height: img.naturalHeight };
  } finally {
    URL.revokeObjectURL(url);
  }
}

async function jpegToWebP(jpegBlob) {
  const { img, width, height } = await decodeToImage(jpegBlob);
  if (!width || !height) throw new Error('Decoded image has zero dimensions.');
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  canvas.getContext('2d').drawImage(img, 0, 0);
  const blob = await new Promise((resolve, reject) => {
    canvas.toBlob(
      (b) => (b ? resolve(b) : reject(new Error('WebP encode returned null.'))),
      'image/webp',
      WEBP_QUALITY,
    );
  });
  return { blob, width, height, bytes: blob.size };
}

async function captureFromPagePath() {
  // Silent hide → capture → restore → cosmetic flash.
  //
  // Waits matter: Chromium's compositor doesn't always paint a fresh
  // frame in the same tick as a DOM mutation, so a naïve hide +
  // immediate capturePage can sample a stale frame that still shows
  // the panels. We do two things to make it robust:
  //   1. Three animation frames + 60 ms wall-clock after the detach
  //      so any pending composite has definitely flushed.
  //   2. TWO capturePage requests: the first forces Chromium to
  //      composite a fresh frame AFTER the detach; the second is
  //      what we actually encode. The first is discarded. This
  //      double-tap is what trading-card apps and screen-recording
  //      tools tend to do. It eliminates flakiness where a cached
  //      pre-detach frame leaks through on fast machines or during
  //      heavy renderer activity.
  const restore = hideOverlays();
  let result;
  try {
    await sleepFrames(3);
    await new Promise((r) => setTimeout(r, 60));
    // Discarded "warm-up" capture. Forces a fresh compositor pass.
    await fetchCaptureJpeg().catch(() => {});
    // Real capture.
    const jpeg = await fetchCaptureJpeg();
    result = await jpegToWebP(jpeg);
  } finally {
    restore();
  }
  playShutterCosmetic();
  return result;
}

// ---- Public API -----------------------------------------------------------

export async function captureMoment() {
  // Canvas path first. If usable, we grab pixels instantly from the
  // WebGL buffer. No panels are in the capture, no panels need to
  // be hidden from the user, and the flash can run as a pure
  // cosmetic after the fact without any timing coordination.
  const canvasResult = await captureFromCanvasPath();
  if (canvasResult) {
    if (import.meta.env?.DEV) console.log('[moments] using CANVAS path');
    playShutterCosmetic();
    return canvasResult;
  }
  // No game canvas available (user is in the hub or a menu). Fall
  // back to the main-process capturePage and rely on the flash to
  // mask the brief overlay hide. The web build has no main process;
  // fail with a message the moments UI surfaces as a toast.
  if (isWeb()) {
    throw new Error('Captures work at the game table only in the browser version.');
  }
  if (import.meta.env?.DEV) console.log('[moments] using capturePage path');
  return captureFromPagePath();
}

// Convert a Blob of image bytes into a base64 string (no data: prefix)
// for transmission over WebSocket. FileReader keeps the heavy lifting
// off the JS string-concat path.
export function blobToBase64(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result);
      const comma = result.indexOf(',');
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });
}

export function base64ToBlob(base64, mime = 'image/webp') {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return new Blob([bytes], { type: mime });
}
